import { useState } from 'react'
import { useI18n } from '../i18n'
import { createPortal } from 'react-dom'
import { X, Wallet } from 'lucide-react'
import QRCodeDisplay from '../utils/qrcode'
import { APPLE_WALLET_ENABLED, addAppleWalletPass } from '../utils/walletPass'

// ★★ v242.88.0 — WALLET-STRAND-1. The long note that stood here was my
//   WRONG diagnosis of TF b34 #13, shipped in v242.87.0 and corrected by
//   Dr. Rami the same day. It is preserved, with the three claims and which
//   two were false, in ORELLA_RULES §1bl and D145 — kept rather than deleted,
//   because a confidently-argued wrong conclusion is more instructive intact.
//   The short version: the endpoint is real and needs no auth, Wallet did take
//   the pass, and the ONLY thing wrong was that an <a href> navigated the web
//   view away from the app.
//   Both wallet flags and the add-a-pass call now live in
//   src/utils/walletPass.js, which also records the production probe that
//   decides them. Nothing about wallets is decided in this file any more.
// v223.9.7: white "orella" wordmark (transparent bg) replaces the text header
import orellaWordmarkWhite from '../assets/logos/orella-wordmark-white-transparent.png'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * WalletPassModal — the designed "visit pass" rendered as a popup.
 *
 * A navy wallet-style card (matching the Apple/Google Wallet design) showing
 * the logistics needed at the front desk: patient, appointment date/time,
 * provider, location, and the check-in QR. PHI-minimized by design.
 *
 * Props:
 *   isOpen, onClose
 *   patientName, providerName, practiceName, addressLine1, addressLine2
 *   dateLabel, timeLabel, token
 */

const NAVY = '#15354F'
const LIGHT_BLUE = '#A7D9FC'

function Field({ label, value, multiline }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: LIGHT_BLUE }}>
        {label}
      </div>
      <div className={`text-white text-[15px] font-semibold ${multiline ? 'whitespace-pre-line leading-snug' : ''}`}>
        {value || '\u2014'}
      </div>
    </div>
  )
}

export default function WalletPassModal({
  isOpen,
  onClose,
  caseId,
  patientName,
  providerName,
  practiceName,
  addressLine1,
  addressLine2,
  dateLabel,
  timeLabel,
  token,
}) {
  const { t } = useI18n()
  // ★ Hooks before the early return — this component returns null when closed,
  //   and a hook after that is a hook-order violation on the next open.
  const [walletBusy, setWalletBusy] = useState(false)
  const [walletError, setWalletError] = useState('')

  if (!isOpen) return null

  const locationValue = [practiceName, addressLine1, addressLine2].filter(Boolean).join('\n')

  const handleAddToWallet = async () => {
    if (walletBusy) return
    setWalletBusy(true)
    setWalletError('')
    try {
      await addAppleWalletPass(caseId)
    } catch (err) {
      console.error('[Wallet] add failed:', err)
      setWalletError(err?.message || 'Could not add the pass.')
    } finally {
      setWalletBusy(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute -top-11 right-0 p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Wallet card */}
        <div className="rounded-[26px] overflow-hidden shadow-2xl" style={{ backgroundColor: NAVY }}>
          {/* Header */}
          <div className="px-6 pt-6 pb-4 flex items-center justify-between">
            <img
              src={orellaWordmarkWhite}
              alt="Orella"
              className="h-6 w-auto select-none"
              draggable={false}
            />
            <span
              className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full"
              style={{ color: NAVY, backgroundColor: LIGHT_BLUE }}
            >
              {t('checkIn.visitPassBadge')}
            </span>
          </div>

          {/* Perforation divider */}
          <div className="mx-6 border-t border-dashed" style={{ borderColor: 'rgba(167,217,252,0.35)' }} />

          {/* Details */}
          <div className="px-6 py-5 space-y-4">
            <Field label="Patient" value={patientName} />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Date" value={dateLabel} />
              <Field label="Time" value={timeLabel} />
            </div>
            <Field label="Clinician" value={providerName} />
            <Field label="Location" value={locationValue} multiline />
          </div>

          {/* QR tile */}
          <div className="px-6 pb-6">
            <div className="rounded-2xl bg-white p-5 flex flex-col items-center">
              {token ? (
                <>
                  <QRCodeDisplay value={token} size={170} color={NAVY} />
                  <p className="mt-3 text-xs text-slate-500">
                    {t('checkIn.checkInCodeLabel', 'Check-in code:')}{' '}
                    <span className="font-bold" style={{ color: NAVY }}>{token}</span>
                  </p>
                </>
              ) : (
                <div className="text-sm text-slate-500 py-10 text-center">
                  {t('checkIn.passCodeUnavailable', 'Pass code unavailable. Pull to refresh and try again.')}
                </div>
              )}
            </div>
            <p className="text-center text-[12px] mt-3" style={{ color: LIGHT_BLUE }}>
              {t('checkIn.showThisPass', 'Show this pass at the front desk when you arrive.')}
            </p>
            {APPLE_WALLET_ENABLED && caseId && token && (
              <button
                onClick={handleAddToWallet}
                disabled={walletBusy}
                data-wallet-add="apple"
                className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-[14px] font-semibold disabled:opacity-60"
                style={{ backgroundColor: '#fff', color: NAVY }}
              >
                {walletBusy ? <OrellaSpinner size={16} tone="inherit" /> : <Wallet size={16} />}
                {walletBusy ? 'Preparing pass…' : 'Add to Apple Wallet'}
              </button>
            )}
            {walletError && (
              <p className="mt-2 text-center text-[12px] text-red-200">{walletError}</p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
